import React from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Alert,
  Button,
  Tag,
  Space,
  Tooltip,
} from 'antd';
import {
  DatabaseOutlined,
  ApiOutlined,
  CloudServerOutlined,
  DesktopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  MailOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

const SystemHealth = ({ embedded = false }) => {
  // Fetch system health data
  const {
    data: healthData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    'system-health',
    async () => {
      try {
        const response = await api.get('/admin/system-health');
        return response.data?.data || response.data || {};
      } catch (error) {
        console.error('Failed to fetch system health:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
  );

  const handleRefresh = () => {
    refetch();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return '#52c41a';
      case 'warning':
        return '#fa8c16';
      case 'critical':
        return '#ff4d4f';
      default:
        return '#d9d9d9';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleOutlined style={{ color: getStatusColor(status) }} />;
      case 'warning':
        return <ExclamationCircleOutlined style={{ color: getStatusColor(status) }} />;
      case 'critical':
        return <CloseCircleOutlined style={{ color: getStatusColor(status) }} />;
      default:
        return <InfoCircleOutlined style={{ color: getStatusColor(status) }} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy':
        return 'Healthy';
      case 'warning':
        return 'Warning';
      case 'critical':
        return 'Critical';
      default:
        return 'Unknown';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return '#52c41a';
    if (percentage >= 60) return '#fa8c16';
    return '#ff4d4f';
  };

  const formatUptime = (uptime) => {
    if (uptime === 'Unknown') return 'Unknown';
    return uptime;
  };

  if (error) {
    return (
      <Alert
        message="Error Loading System Health"
        description="Failed to load system health data. Please try again later."
        type="error"
        showIcon
        action={
          <Button size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }
        style={{ marginBottom: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: embedded ? 0 : '24px', maxWidth: '100%', overflow: 'hidden' }}>
      {!embedded && (
        <Title level={2}>
          <CloudServerOutlined /> System Health
        </Title>
      )}

      {/* Overall Status */}
      <Card style={{ marginBottom: 24, overflow: 'hidden' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Overall Status"
              value={getStatusText(healthData?.overall_status || 'unknown')}
              prefix={getStatusIcon(healthData?.overall_status || 'unknown')}
              valueStyle={{
                color: getStatusColor(healthData?.overall_status || 'unknown'),
                fontSize: '22px',
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Health Score"
              value={healthData?.health_percentage || 0}
              suffix="%"
              valueStyle={{
                color: getProgressColor(healthData?.health_percentage || 0),
                fontSize: '22px',
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Healthy Services"
              value={`${healthData?.healthy_services || 0}/${healthData?.total_services || 0}`}
              valueStyle={{ fontSize: '22px' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Tooltip title="Last Updated">
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Last Updated:{' '}
                    {healthData?.last_updated
                      ? dayjs(healthData.last_updated).format('MMM DD, YYYY HH:mm:ss')
                      : 'Never'}
                  </span>
                </Tooltip>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={isLoading}>
                  Refresh
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Service Status Cards */}
      <Row gutter={[16, 16]}>
        {/* Database Health */}
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space>
                <DatabaseOutlined /> Database
                <Tag color={getStatusColor(healthData?.services?.database?.status)}>
                  {getStatusText(healthData?.services?.database?.status)}
                </Tag>
              </Space>
            }
            style={{ overflow: 'hidden' }}
          >
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic
                  title="Connection"
                  value={healthData?.services?.database?.connection || 'Unknown'}
                  valueStyle={{
                    color:
                      healthData?.services?.database?.connection === 'connected'
                        ? '#52c41a'
                        : '#ff4d4f',
                    fontSize: '16px',
                  }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Response Time"
                  value={`${healthData?.services?.database?.response_time_ms || 0}ms`}
                  valueStyle={{
                    color:
                      healthData?.services?.database?.response_time_ms < 1000
                        ? '#52c41a'
                        : '#fa8c16',
                    fontSize: '16px',
                  }}
                />
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic
                  title="Driver"
                  value={healthData?.services?.database?.driver || 'Unknown'}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Database"
                  value={healthData?.services?.database?.database || 'Unknown'}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Statistic
                  title="Tables"
                  value={healthData?.services?.database?.tables || 0}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>
            {healthData?.services?.database?.message && (
              <Alert
                message={healthData.services.database.message}
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>

        {/* API Health */}
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space>
                <ApiOutlined /> API
                <Tag color={getStatusColor(healthData?.services?.api?.status)}>
                  {getStatusText(healthData?.services?.api?.status)}
                </Tag>
              </Space>
            }
            style={{ overflow: 'hidden' }}
          >
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic
                  title="Response Time"
                  value={`${healthData?.services?.api?.response_time_ms || 0}ms`}
                  valueStyle={{
                    color:
                      healthData?.services?.api?.response_time_ms < 500 ? '#52c41a' : '#fa8c16',
                    fontSize: '16px',
                  }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Endpoints"
                  value={healthData?.services?.api?.endpoints_working ? 'Working' : 'Failed'}
                  valueStyle={{
                    color: healthData?.services?.api?.endpoints_working ? '#52c41a' : '#ff4d4f',
                    fontSize: '16px',
                  }}
                />
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic
                  title="Users"
                  value={healthData?.services?.api?.users_count || 0}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Orders"
                  value={healthData?.services?.api?.orders_count || 0}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>
            {healthData?.services?.api?.error && (
              <Alert
                message={healthData.services.api.error}
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>

        {/* Server Health */}
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space>
                <DesktopOutlined /> Server
                <Tag color={getStatusColor(healthData?.services?.server?.status)}>
                  {getStatusText(healthData?.services?.server?.status)}
                </Tag>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <div style={{ marginBottom: 8 }}>Memory Usage</div>
                  <Progress
                    percent={healthData?.services?.server?.memory_usage?.percentage || 0}
                    status={
                      healthData?.services?.server?.memory_usage?.percentage > 80
                        ? 'exception'
                        : 'normal'
                    }
                    strokeColor={getProgressColor(
                      healthData?.services?.server?.memory_usage?.percentage || 0,
                    )}
                  />
                  <div style={{ fontSize: '14px', color: '#666', marginTop: 4 }}>
                    {healthData?.services?.server?.memory_usage?.used || '0 B'} /{' '}
                    {healthData?.services?.server?.memory_usage?.limit || 'Unknown'}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <div style={{ marginBottom: 8 }}>Disk Usage</div>
                  <Progress
                    percent={healthData?.services?.server?.disk_usage?.percentage || 0}
                    status={
                      healthData?.services?.server?.disk_usage?.percentage > 80
                        ? 'exception'
                        : 'normal'
                    }
                    strokeColor={getProgressColor(
                      healthData?.services?.server?.disk_usage?.percentage || 0,
                    )}
                  />
                  <div style={{ fontSize: '14px', color: '#666', marginTop: 4 }}>
                    {healthData?.services?.server?.disk_usage?.used || '0 B'} /{' '}
                    {healthData?.services?.server?.disk_usage?.total || 'Unknown'}
                  </div>
                </div>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic
                  title="Load (1min)"
                  value={healthData?.services?.server?.server_load?.['1_min'] || 0}
                  precision={2}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Load (5min)"
                  value={healthData?.services?.server?.server_load?.['5_min'] || 0}
                  precision={2}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Load (15min)"
                  value={healthData?.services?.server?.server_load?.['15_min'] || 0}
                  precision={2}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="PHP Version"
                  value={healthData?.services?.server?.php_version || 'Unknown'}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Laravel Version"
                  value={healthData?.services?.server?.laravel_version || 'Unknown'}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Statistic
                  title="Uptime"
                  value={formatUptime(healthData?.services?.server?.uptime || 'Unknown')}
                />
              </Col>
            </Row>
            {healthData?.services?.server?.issues &&
              healthData.services.server.issues.length > 0 && (
                <Alert
                  message="Server Issues Detected"
                  description={healthData.services.server.issues.join(', ')}
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
          </Card>
        </Col>

        {/* Cache Health */}
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space>
                <CloudServerOutlined /> Cache
                <Tag color={getStatusColor(healthData?.services?.cache?.status)}>
                  {getStatusText(healthData?.services?.cache?.status)}
                </Tag>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic title="Driver" value={healthData?.services?.cache?.driver || 'None'} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Response Time"
                  value={`${healthData?.services?.cache?.response_time_ms || 0}ms`}
                  valueStyle={{
                    color:
                      healthData?.services?.cache?.response_time_ms < 200 ? '#52c41a' : '#fa8c16',
                  }}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Statistic
                  title="Test Status"
                  value={healthData?.services?.cache?.test_passed ? 'Passed' : 'Failed'}
                  valueStyle={{
                    color: healthData?.services?.cache?.test_passed ? '#52c41a' : '#ff4d4f',
                  }}
                />
              </Col>
            </Row>
            {healthData?.services?.cache?.message && (
              <Alert
                message={healthData.services.cache.message}
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>

        {/* Storage Health */}
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space>
                <FolderOutlined /> Storage
                <Tag color={getStatusColor(healthData?.services?.storage?.status)}>
                  {getStatusText(healthData?.services?.storage?.status)}
                </Tag>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Storage Path"
                  value={healthData?.services?.storage?.storage_path || 'Unknown'}
                  valueStyle={{ fontSize: '14px', wordBreak: 'break-all' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Storage Writable"
                  value={healthData?.services?.storage?.storage_writable ? 'Yes' : 'No'}
                  valueStyle={{
                    color: healthData?.services?.storage?.storage_writable ? '#52c41a' : '#ff4d4f',
                  }}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Public Path"
                  value={healthData?.services?.storage?.public_path || 'Unknown'}
                  valueStyle={{ fontSize: '14px', wordBreak: 'break-all' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Public Writable"
                  value={healthData?.services?.storage?.public_writable ? 'Yes' : 'No'}
                  valueStyle={{
                    color: healthData?.services?.storage?.public_writable ? '#52c41a' : '#ff4d4f',
                  }}
                />
              </Col>
            </Row>
            {healthData?.services?.storage?.error && (
              <Alert
                message="Storage Issues Detected"
                description={healthData.services.storage.error}
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>

        {/* External Services */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <MailOutlined /> External Services
              </Space>
            }
            style={{ overflow: 'hidden' }}
          >
            <Row gutter={[16, 16]}>
              {Object.entries(healthData?.services?.services || {}).map(([service, data]) => (
                <Col xs={24} sm={12} lg={8} key={service}>
                  <Card
                    size="small"
                    style={{
                      borderColor: getStatusColor(data?.status),
                      backgroundColor:
                        data?.status === 'healthy'
                          ? '#f6ffed'
                          : data?.status === 'warning'
                            ? '#fffbe6'
                            : '#fff1f0',
                      overflow: 'hidden',
                    }}
                  >
                    <Row gutter={[8, 8]} align="middle">
                      <Col span={24}>
                        <div
                          style={{
                            fontWeight: 'bold',
                            textTransform: 'capitalize',
                            marginBottom: 8,
                          }}
                        >
                          {service}
                        </div>
                        <Tag color={getStatusColor(data?.status)} style={{ marginBottom: 8 }}>
                          {getStatusText(data?.status)}
                        </Tag>
                        <div style={{ fontSize: '14px', color: '#666', wordBreak: 'break-all' }}>
                          Driver: {data?.driver || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Configured: {data?.configured ? 'Yes' : 'No'}
                        </div>
                      </Col>
                    </Row>
                    {data?.error && (
                      <Alert
                        message="Service Error"
                        description={data.error}
                        type="warning"
                        showIcon
                        size="small"
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SystemHealth;
